import heroImage from "@/assets/images/about/yolk-intro.jpg";
import introVideo from "@/assets/videos/about/TCGL-Intro.mp4";
import { Footer, Header } from "@/components/shared";
import { updatePageSEO } from "@/utils/seo";
import { ChevronLeft, ChevronRight, Target } from "lucide-react";
import { useEffect, useRef } from "react";

const portfolio = [
  {
    title: "Barking Riverside",
    description:
      "Summer Pop-up programming for emerging live-work-play destination. 8 weeks of creative events targeting young professionals and local families. Exclusive partnerships with UK independents.",
    metrics: "8 weeks · 2 activations per week",
    color: "from-blue-500 to-blue-600",
  },
  {
    title: "First Street, Manchester",
    description:
      "Winter activation programme with CBRE at Manchester's HOME arts venue. Created social connections for 1000+ resident workers while driving footfall to on-site amenities.",
    metrics: "2 months · 1000+ workers engaged",
    color: "from-purple-500 to-purple-600",
  },
  {
    title: "St Helen's Council Partnership",
    description:
      "Alternative Christmas experience supporting local retailers. 4 weeks to delivery, 4 activations per week. 3000+ attendees, 50+ local partners, 92% booking capacity.",
    metrics: "3000+ attendees · 92% capacity",
    color: "from-green-500 to-green-600",
  },
  {
    title: "Bumble App Launch",
    description:
      "6-month Manchester launch campaign for Whitney Wolfe Herd's dating app. Bi-monthly experiential events driving more downloads than any other regional city.",
    metrics: "6 months · Best performing regional launch",
    color: "from-orange-500 to-orange-600",
  },
  {
    title: "Ace & Tate Flagship",
    description:
      "Brand manifesto launch party at Manchester flagship store. Curated local partnerships including Reform Radio, independent food suppliers and craft producers.",
    metrics: "One-off activation · Premium brand partnership",
    color: "from-pink-500 to-pink-600",
  },
  {
    title: "Colchester Business Park",
    description:
      "Post-Covid workplace wellbeing programme. 6 weeks of alfresco activations reconnecting 600 isolated workers through fitness, food and creative workshops.",
    metrics: "600 workers · 3 activations per week",
    color: "from-teal-500 to-teal-600",
  },
  {
    title: "Float Festival",
    description:
      'Wellness festival reflecting our values: "In a World of Sink or Swim, let us be your Float." Sell-out event with national PR coverage in Women\'s Health and DOSE.',
    metrics: "Sell-out event · National PR coverage",
    color: "from-indigo-500 to-indigo-600",
  },
  {
    title: "Pan Macmillan Book Launch",
    description:
      "Regional launch tour for 'The Charmed Life of Alex Moore'. Curated speaker panels exploring success and ambition across UK cities.",
    metrics: "Multi-city tour · Publisher partnership",
    color: "from-rose-500 to-rose-600",
  },
  {
    title: "Wallpaper* x Allied London",
    description:
      "Collaborated with Wallpaper* Composed on conceptual interior design for Manchester's iconic Grade II-listed London Road Fire Station. Featured in the October 2017 issue, visualising Allied London's vision for the landmark 1906 building's regeneration.",
    metrics: "National PR coverage · Wallpaper* Magazine feature",
    color: "from-cyan-500 to-cyan-600",
    link: "https://www.wallpaper.com/design/wallpaper-composed-london-road-mancheser",
  },
];

const YolkStudios = () => {
  const portfolioRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    updatePageSEO("/about");
  }, []);

  const scrollPortfolio = (direction: "left" | "right") => {
    if (portfolioRef.current) {
      portfolioRef.current.scrollBy({
        left: direction === "left" ? -320 : 320,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />

      {/* Hero */}
      <div className="relative min-h-screen flex items-end overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${heroImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

        <div className="relative z-10 max-w-5xl mx-auto px-6 pb-24 pt-40 w-full">
          <p className="text-xs tracking-widest uppercase opacity-50 mb-4">
            Placemaking Studio
          </p>
          <h1
            className="font-black uppercase leading-none mb-6"
            style={{
              fontFamily: "'Bricolage Grotesque', sans-serif",
              fontSize: "clamp(3rem, 9vw, 7rem)",
              letterSpacing: "-0.03em",
            }}
          >
            Yolk Studios
          </h1>
          <p
            className="text-xl md:text-2xl max-w-xl leading-relaxed"
            style={{ opacity: 0.6 }}
          >
            We make places worth being in.
          </p>
        </div>
      </div>

      {/* Studio intro */}
      <div className="bg-black py-24">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div>
              <p
                className="text-xs tracking-widest uppercase mb-6"
                style={{ opacity: 0.4 }}
              >
                What we do
              </p>
              <h2
                className="font-bold uppercase text-3xl md:text-4xl leading-tight mb-8"
                style={{
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  letterSpacing: "-0.02em",
                }}
              >
                Turning space into cultural story
              </h2>
              <p className="text-gray-400 leading-relaxed text-lg">
                Yolk Studios is the activation arm of The Content Group. We
                design and deliver cultural programming that gives places a
                reason to exist beyond their footprint - events, experiences and
                creative partnerships that turn buildings into destinations and
                destinations into communities.
              </p>
            </div>
            <div className="space-y-8 pt-12">
              {[
                {
                  label: "Placemaking",
                  text: "Cultural programmes that anchor new developments, retail precincts and business districts into their local context.",
                },
                {
                  label: "Brand Activation",
                  text: "Experiential launches and ongoing campaigns that build community around a brand, not just awareness of it.",
                },
                {
                  label: "Workplace Culture",
                  text: "Wellbeing and social programming for commercial estates - turning isolated workers into a genuine occupier community.",
                },
              ].map((item) => (
                <div key={item.label} className="border-t border-white/10 pt-6">
                  <p
                    className="text-xs tracking-widest uppercase mb-2"
                    style={{
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      opacity: 0.45,
                    }}
                  >
                    {item.label}
                  </p>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Track record strip */}
      <div className="bg-white/5 border-y border-white/10 py-12">
        <div className="max-w-4xl mx-auto px-6">
          <p
            className="text-xs tracking-widest uppercase mb-8 text-center"
            style={{ opacity: 0.4 }}
          >
            Built on 25+ years working with
          </p>
          <div className="flex flex-wrap justify-center gap-x-10 gap-y-3">
            {[
              "CBRE",
              "Allied London",
              "The Collective",
              "Ace & Tate",
              "Bumble",
              "Pan Macmillan",
              "Wallpaper*",
            ].map((name) => (
              <span
                key={name}
                className="text-sm tracking-wider uppercase"
                style={{
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  opacity: 0.55,
                }}
              >
                {name}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Case Studies */}
      <div className="bg-black py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p
                className="text-xs tracking-widest uppercase mb-3"
                style={{ opacity: 0.4 }}
              >
                Selected work
              </p>
              <h2
                className="font-bold uppercase text-3xl md:text-4xl"
                style={{
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  letterSpacing: "-0.02em",
                }}
              >
                Case Studies
              </h2>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => scrollPortfolio("left")}
                className="p-3 rounded-full border border-white/15 hover:border-white/40 transition-colors"
                aria-label="Scroll left"
              >
                <ChevronLeft className="w-5 h-5 text-gray-400" />
              </button>
              <button
                onClick={() => scrollPortfolio("right")}
                className="p-3 rounded-full border border-white/15 hover:border-white/40 transition-colors"
                aria-label="Scroll right"
              >
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>

          <div
            ref={portfolioRef}
            className="flex space-x-5 overflow-x-auto pb-4"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            <style>{`div::-webkit-scrollbar{display:none}`}</style>
            {portfolio.map((project, index) => (
              <div
                key={index}
                className={`flex-shrink-0 w-72 ${project.link ? "cursor-pointer" : ""}`}
                onClick={() => project.link && window.open(project.link, "_blank")}
              >
                <div className="border border-white/10 rounded-2xl overflow-hidden hover:border-white/25 transition-colors h-full">
                  <div
                    className={`h-40 bg-gradient-to-br ${project.color} flex items-center justify-center`}
                  >
                    <Target className="w-10 h-10 text-white/70" />
                  </div>
                  <div className="p-5">
                    <h3
                      className="font-bold uppercase text-sm mb-3 tracking-wide"
                      style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}
                    >
                      {project.title}
                    </h3>
                    <p className="text-gray-500 text-xs leading-relaxed mb-4">
                      {project.description}
                    </p>
                    <span className="text-xs tracking-wider uppercase text-white/35 border border-white/15 px-3 py-1 rounded-full">
                      {project.metrics}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Video */}
      <div className="bg-black py-20 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6">
          <p
            className="text-xs tracking-widest uppercase mb-4 text-center"
            style={{ opacity: 0.4 }}
          >
            Showreel
          </p>
          <h2
            className="text-2xl md:text-3xl font-bold uppercase text-center mb-10"
            style={{
              fontFamily: "'Bricolage Grotesque', sans-serif",
              letterSpacing: "-0.02em",
            }}
          >
            See it in action
          </h2>
          <div className="rounded-2xl overflow-hidden border border-white/10">
            <video controls className="w-full h-auto" poster={heroImage}>
              <source src={introVideo} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-black py-24 border-t border-white/10">
        <div className="max-w-xl mx-auto px-6 text-center">
          <h2
            className="text-3xl md:text-4xl font-black uppercase mb-4"
            style={{
              fontFamily: "'Bricolage Grotesque', sans-serif",
              letterSpacing: "-0.02em",
            }}
          >
            Got a space that needs a story?
          </h2>
          <p className="text-gray-500 mb-8">Let's talk about what's possible.</p>
          <a
            href="mailto:hello@thecontentgroup.co"
            className="inline-block text-sm tracking-widest uppercase border border-white/30 hover:border-white px-8 py-4 rounded-full transition-colors"
            style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}
          >
            Get in touch
          </a>
        </div>
      </div>

      <Footer variant="dark" />
    </div>
  );
};

export default YolkStudios;
